import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Copy, ShieldAlert, X } from 'lucide-react'
import { generateHeaderSnippet, generateLinuxInstallCommand, generateMcpSnippet, type LinuxArch } from '../../engine/mcp/crypto'
import { getWriteSeed } from '../../engine/mcp/utils'
import { buildCreatePayload } from '../../engine/mcp/connectionEditing'
import { useMcpConnections } from '../../hooks/useMcpConnections'
import { useEngineManager, useRegistry } from '../../hooks/useWorkspace'
import './CreateMcpConnectionModal.css'

interface CreateMcpConnectionModalProps {
    isOpen: boolean
    onClose: () => void
    initialWorkspaceId?: string
}

type Step = 'config' | 'result'
type McpClient = 'cursor' | 'antigravity' | 'header' | 'linux'

export default function CreateMcpConnectionModal({ isOpen, onClose, initialWorkspaceId }: CreateMcpConnectionModalProps) {
    const { entries } = useRegistry()
    const engine = useEngineManager()
    const { create, getToken } = useMcpConnections()
    const identity = engine.getIdentity()

    const [step, setStep] = useState<Step>('config')
    const [name, setName] = useState('')
    const initialSelection = useRef(initialWorkspaceId ? new Set([initialWorkspaceId]) : new Set<string>())
    const [selectedBlobIds, setSelectedBlobIds] = useState<Set<string>>(initialWorkspaceId ? new Set([initialWorkspaceId]) : new Set())
    // Default write access ON for all workspaces that have write capability
    const [writeAccess, setWriteAccess] = useState<Set<string>>(() => {
        const writableIds = new Set<string>()
        for (const entry of entries) {
            if (getWriteSeed(entry.capability)) {
                writableIds.add(entry.blobId)
            }
        }
        return writableIds
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [resultToken, setResultToken] = useState<string | null>(null)
    const [resultEntry, setResultEntry] = useState<any>(null)
    const [error, setError] = useState<{ message: string; details?: string } | null>(null)
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [mcpClient, setMcpClient] = useState<McpClient>('cursor')
    const [linuxArch, setLinuxArch] = useState<LinuxArch>('x86_64')

    const sortedEntries = useMemo(() => {
        const pinned = initialSelection.current
        return [...entries].sort((a, b) => {
            const aPin = pinned.has(a.blobId)
            const bPin = pinned.has(b.blobId)
            if (aPin && !bPin) return -1
            if (!aPin && bPin) return 1
            return a.label.localeCompare(b.label)
        })
    }, [entries])

    const toggleSelection = (blobId: string) => {
        const entry = entries.find(item => item.blobId === blobId)
        if (!selectedBlobIds.has(blobId) && entry && !getWriteSeed(entry.capability)) {
            return
        }
        const next = new Set(selectedBlobIds)
        if (next.has(blobId)) {
            next.delete(blobId)
            if (writeAccess.has(blobId)) {
                const nextWrite = new Set(writeAccess)
                nextWrite.delete(blobId)
                setWriteAccess(nextWrite)
            }
        } else {
            next.add(blobId)
            // Auto-enable write for newly added workspaces that have write capability
            // (consistent with Edit modal behavior - deselect+reselect must restore write)
            const entry = entries.find(item => item.blobId === blobId)
            if (entry && getWriteSeed(entry.capability)) {
                const nextWrite = new Set(writeAccess)
                nextWrite.add(blobId)
                setWriteAccess(nextWrite)
            }
        }
        setSelectedBlobIds(next)
    }

    const toggleWrite = (e: React.MouseEvent, blobId: string) => {
        e.stopPropagation()
        const entry = entries.find(item => item.blobId === blobId)
        if (!entry) return

        if (!getWriteSeed(entry.capability)) return // Button is disabled, guard only

        const next = new Set(writeAccess)
        if (next.has(blobId)) next.delete(blobId)
        else next.add(blobId)
        setWriteAccess(next)
    }

    const handleCreate = async () => {
        if (!name.trim()) {
            setError({ message: 'Please enter a connection name' })
            return
        }
        if (selectedBlobIds.size === 0) {
            setError({ message: 'Please select at least one workspace' })
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const { configs, mcpReaderSeeds, mcpWriterSeeds } = buildCreatePayload(
                selectedBlobIds,
                writeAccess,
                entries.map(e => ({ blobId: e.blobId, label: e.label, emoji: e.emoji, capability: e.capability })),
            )

            const { entry, token } = await create(name, configs, mcpReaderSeeds, mcpWriterSeeds)
            setResultToken(token)
            setResultEntry(entry)
            setStep('result')
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err)
            setError({ message: 'Failed to create connection.', details: msg })
        } finally {
            setIsSubmitting(false)
        }
    }

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text)
        setCopiedField(field)
        setTimeout(() => setCopiedField(null), 2000)
    }

    const connectionToken = useMemo(() => {
        if (!resultEntry) return resultToken ?? ''
        return getToken(resultEntry)
    }, [resultEntry, resultToken, getToken])

    const snippet = useMemo(() => {
        if (!resultEntry) return ''
        if (mcpClient === 'linux') {
            return generateLinuxInstallCommand(resultEntry, resultEntry.label || name || 'secureblob', linuxArch)
        }
        if (mcpClient === 'header') {
            return generateHeaderSnippet(resultEntry.configBlobId, connectionToken)
        }
        return generateMcpSnippet(resultEntry, mcpClient)
    }, [resultEntry, mcpClient, linuxArch, name, connectionToken])

    if (!isOpen) return null

    return createPortal(
        <div className="mcp-modal-overlay" onClick={onClose}>
            <div className="mcp-modal" onClick={e => e.stopPropagation()}>
                <div className="mcp-modal-header">
                    <div className="mcp-modal-title">Create MCP Connection</div>
                    <button className="mcp-modal-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="mcp-modal-body">
                    {step === 'config' ? (
                        <>
                            <div className="mcp-form-group">
                                <label className="mcp-label">Connection name</label>
                                <input
                                    className="mcp-input"
                                    placeholder="my-team"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            <div className="mcp-form-group">
                                <label className="mcp-label">Workspaces</label>
                                <div className="mcp-workspace-list">
                                    {sortedEntries.map((entry) => {
                                        const isSelected = selectedBlobIds.has(entry.blobId)
                                        const canWrite = !!getWriteSeed(entry.capability)
                                        const writeEnabled = writeAccess.has(entry.blobId)
                                        return (
                                            <div
                                                key={entry.blobId}
                                                className="mcp-ws-item"
                                                onClick={() => toggleSelection(entry.blobId)}
                                                title={!canWrite ? 'MCP connections now require writer/manage access for revocable reader credentials.' : undefined}
                                            >
                                                <div className="mcp-checkbox">{isSelected ? <Check size={14} /> : null}</div>
                                                <div className="mcp-ws-emoji">{entry.emoji || '📁'}</div>
                                                <div className="mcp-ws-info">
                                                    <div className="mcp-ws-label">{entry.label}</div>
                                                    <div className="mcp-ws-hash">{entry.blobId}</div>
                                                </div>
                                                <div className="mcp-ws-perms">
                                                    <button
                                                        type="button"
                                                        className={`mcp-perm-btn ${writeEnabled ? 'active' : ''}`}
                                                        onClick={(e) => toggleWrite(e, entry.blobId)}
                                                        disabled={!canWrite}
                                                        title={!canWrite ? 'You are a read-only member of this workspace' : undefined}
                                                    >
                                                        write
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {error && (
                                <div className="mcp-error-block">
                                    <div className="mcp-error-header">
                                        <ShieldAlert size={14} />
                                        <span>{error.message}</span>
                                    </div>
                                    {error.details && (
                                        <details className="mcp-error-details">
                                            <summary>Show details</summary>
                                            <pre>{error.details}</pre>
                                        </details>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <div className="mcp-success-icon">
                                    <Check size={24} color="#10b981" />
                                </div>
                                <h3 style={{ color: 'var(--color-text-primary)', margin: '0 0 4px' }}>{name || 'Connection'} Ready</h3>
                                <p style={{ color: 'var(--color-text-tertiary)', fontSize: 14, margin: 0 }}>Copy configuration (MCP clients) or one-line Linux install command.</p>
                            </div>

                            <div className="mcp-client-toggle">
                                <button className={`mcp-client-btn ${mcpClient === 'cursor' ? 'active' : ''}`} onClick={() => setMcpClient('cursor')}><span className="mcp-client-icon">⌘</span> Cursor</button>
                                <button className={`mcp-client-btn ${mcpClient === 'antigravity' ? 'active' : ''}`} onClick={() => setMcpClient('antigravity')}><span className="mcp-client-icon">◆</span> Antigravity</button>
                                <button className={`mcp-client-btn ${mcpClient === 'header' ? 'active' : ''}`} onClick={() => setMcpClient('header')}><span className="mcp-client-icon">#</span> MCP w header</button>
                                <button className={`mcp-client-btn ${mcpClient === 'linux' ? 'active' : ''}`} onClick={() => setMcpClient('linux')}><span className="mcp-client-icon">🐧</span> Linux</button>
                            </div>



                            {mcpClient === 'linux' && (
                                <div className="mcp-client-toggle mcp-client-toggle-arch" style={{ marginTop: -10 }}>
                                    <button className={`mcp-client-btn ${linuxArch === 'x86_64' ? 'active' : ''}`} onClick={() => setLinuxArch('x86_64')}>x86_64</button>
                                    <button className={`mcp-client-btn ${linuxArch === 'arm64' ? 'active' : ''}`} onClick={() => setLinuxArch('arm64')}>arm64</button>
                                </div>
                            )}

                            <div className="mcp-form-group">
                                <label className="mcp-label">{mcpClient === 'linux' ? 'Linux one-line install command' : 'Configuration snippet'}</label>
                                <div className="mcp-result-block">
                                    {snippet}
                                    <button className={`mcp-copy-btn ${copiedField === 'snippet' ? 'copied' : ''}`} onClick={() => copyToClipboard(snippet, 'snippet')} title="Copy">
                                        {copiedField === 'snippet' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                    </button>
                                </div>
                            </div>

                            {mcpClient === 'linux' && (
                                <div className="mcp-form-group" style={{ marginTop: -8 }}>
                                    <label className="mcp-label">Connection token</label>
                                    <div className="mcp-result-block">
                                        {connectionToken}
                                        <button className={`mcp-copy-btn ${copiedField === 'token' ? 'copied' : ''}`} onClick={() => copyToClipboard(connectionToken, 'token')} title="Copy token">
                                            {copiedField === 'token' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mcp-guide">
                                <strong>How to install:</strong>
                                {mcpClient === 'linux' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Copy one-line command and run in terminal.</li>
                                        <li>Script downloads the latest binary (first run triggers server-side build ~1 min, subsequent runs use cache).</li>
                                        <li>FUSE mounts at <code>~/&lt;name&gt;</code> — files appear immediately. Ctrl-C to stop.</li>
                                    </ol>
                                ) : mcpClient === 'cursor' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open <strong>Cursor</strong></li>
                                        <li>Go to <strong>Settings {'>'} Features {'>'} MCP</strong></li>
                                        <li>Click &quot;Add new MCP server&quot;</li>
                                        <li>Paste the configuration above</li>
                                        <li>Cursor will open a browser — sign in with your Passkey to approve</li>
                                    </ol>
                                ) : mcpClient === 'antigravity' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open <strong>Antigravity</strong> settings</li>
                                        <li>Edit your <strong>mcp_config.json</strong></li>
                                        <li>Add the <code>secureblob</code> entry from above into <code>mcpServers</code></li>
                                        <li>Restart the MCP server connection</li>
                                        <li>Antigravity will open a browser — sign in with your Passkey to approve</li>
                                    </ol>
                                ) : (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open your MCP client config file</li>
                                        <li>Add the <code>secureblob</code> entry from above into <code>mcpServers</code></li>
                                        <li>Keep the <code>X-SecureBlob-Key</code> header secret private</li>
                                        <li>Reload or restart the MCP server connection</li>
                                    </ol>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="mcp-modal-footer">
                    {step === 'config' ? (
                        <>
                            <button className="mcp-btn mcp-btn-secondary" onClick={onClose}>Cancel</button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {identity && (
                                    <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                                        Creating as {identity.emoji} {identity.name}
                                    </span>
                                )}
                                <button className="mcp-btn mcp-btn-primary" onClick={handleCreate} disabled={isSubmitting}>
                                    {isSubmitting ? 'Creating...' : 'Create Connection'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <button className="mcp-btn mcp-btn-primary" onClick={onClose}>Done</button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    )
}
